import { has, isArray } from 'lodash';
import React from 'react';
import styled from 'styled-components';

import InfiniteTabs from '../../Common/InfiniteTabs';
import Tab from '../../../Tab';
import TicketCard from '../TicketCard';
import EmptyTicketsList from '../EmptyTicketsList';
import TicketCardSkeleton from '../TicketCardSkeleton';
import { IInfiniteList } from '../../../InfiniteList/types';
import { InfiniteTabsProps } from '../../Common/InfiniteTabs/types';
import { ITicketCard } from '../TicketCard/types';
import {
  MINIMUM_BATCH_SIZE,
  THRESHOLD_DEFAULT,
} from '../../../InfiniteList/constants';

const TICKET_HEIGHT = 160;

interface TicketListProps {
  className?: string;
  itemCount: IInfiniteList['itemCount'];
  minimumBatchSize: IInfiniteList['minimumBatchSize'];
  onLoadRequest: IInfiniteList['onLoadRequest'];
  onSelect: (activeId: number | string) => void;
  threshold: IInfiniteList['threshold'];
  tickets: Array<Omit<ITicketCard, 'planReason' | 'className'>>;
  value: InfiniteTabsProps['value'];
  hasNextPage: boolean;
  isNextPageLoading: boolean;
  isLoadingList: boolean;
  planReasons: Array<ITicketCard['planReason']>;
}

interface TicketListState {
  activeId: TicketListProps['value'];
}

class TicketList extends React.PureComponent<TicketListProps, TicketListState> {
  static defaultProps: Partial<TicketListProps> = {
    className: '',
    itemCount: 0,
    minimumBatchSize: MINIMUM_BATCH_SIZE,
    threshold: THRESHOLD_DEFAULT,
    value: null,
  };
  static getDerivedStateFromProps(
    nextProps: Readonly<TicketListProps>,
    prevState: TicketListState
  ) {
    const { value } = nextProps;
    const { activeId } = prevState;

    if (value !== activeId) {
      return { activeId: value };
    }
    return null;
  }

  static getItemKey(index, data) {
    if (!data || !has(data, `${index}.ticket.id`)) {
      return null;
    }

    return data[index].ticket.id;
  }

  constructor(props) {
    super(props);

    this.state = {
      activeId: props.value,
    };

    this.handleSelect = this.handleSelect.bind(this);
    this.isItemLoaded = this.isItemLoaded.bind(this);
  }

  handleSelect(activeId) {
    const { onSelect } = this.props;

    this.setState(
      {
        activeId,
      },
      () => onSelect(activeId)
    );
  }

  isItemLoaded(index) {
    const { tickets } = this.props;
    return has(tickets, `${index}.ticket.id`);
  }

  renderSkeletons() {
    return new Array(5).fill(null).map(el => <StyledTicketCardSkeleton />);
  }

  render() {
    const {
      className,
      itemCount,
      minimumBatchSize,
      onLoadRequest,
      tickets,
      threshold,
      hasNextPage,
      isNextPageLoading,
      isLoadingList,
      planReasons,
    } = this.props;
    const { activeId } = this.state;

    return (
      <StyledTicketList className={className}>
        {isArray(tickets) && !tickets.length && !isLoadingList && (
          <EmptyTicketsList />
        )}
        {isLoadingList && this.renderSkeletons()}
        <InfiniteTabs
          hasNextPage={hasNextPage}
          isNextPageLoading={isNextPageLoading}
          isItemLoaded={this.isItemLoaded}
          itemCount={itemCount}
          itemData={tickets}
          itemKey={TicketList.getItemKey}
          itemSize={TICKET_HEIGHT}
          minimumBatchSize={minimumBatchSize}
          onLoadRequest={onLoadRequest}
          onSelect={this.handleSelect}
          threshold={threshold}
          value={activeId}
        >
          {({ data, index, style }) => {
            if (!data || !data[index]) {
              return null;
            }

            const { client, ticket } = data[index];
            const planReason = (planReasons || []).find(
              reason => reason?.id === ticket.plannedReasonId
            );

            const { id, read, priority } = ticket;
            const disabled = !id || !client;

            return (
              <StyledTab
                read={read}
                style={style}
                value={id}
                priority={priority}
                disabled={disabled}
              >
                <TicketCard
                  planReason={planReason}
                  client={client}
                  ticket={ticket}
                />
              </StyledTab>
            );
          }}
        </InfiniteTabs>
      </StyledTicketList>
    );
  }
}

const StyledTicketList = styled.div`
  height: 100%;
  width: 100%;
`;

const StyledTab = styled(Tab)`
  border: none;
  background-color: #f5f3f2;

  ${({ read, priority }) =>
    !read &&
    `
      background-color: white;
      animation: ${priority ? 'pulsing 6s infinite ease-in-out' : 'none'};
  `}

  ${({ selected }) =>
    selected &&
    `
    background-color: #fff;
  `}

  @keyframes pulsing {
    from {
      background-color: #fefad0;
    }
    to {
      background-color: #f5fed0;
    }
  }
`;

const StyledTicketCardSkeleton = styled(TicketCardSkeleton)`
  margin-bottom: 12px;
`;

export default TicketList;
